import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import {
  FormBuilder,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AppointmentAuthorizationService,
  AppointmentModel,
  AUTHORIZATION_STATUS,
  AuthorizationModel,
  AuthorizationRulesService,
  AuthorizationStatusPullData,
  AvailityServiceReview,
  ClinicService,
  CrudService,
  CustomFieldService,
  filterAction,
  findInsuranceForAccount,
  InsuranceProviderConfiguration,
  InsuranceProviderConfigurationService,
  InsuranceProviderLookupsService,
  NecessityService,
  NecessityType,
  OutdatedFlags,
  Patient,
  PATIENT_ACCOUNT_PROCEDURE_CLEARANCE_STATUS,
  PatientAccountClearanceModel,
  PatientInsurance,
  PatientInsuranceType,
  PatientPalRestError,
  PermissionsService,
  PortalModel,
  PortalQuestion,
  SaveAuthorizationParams,
  toSafeUiMessage,
  updateFormValidity,
  WebsocketAction,
  WebSocketService,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { filter as _filter, flatten, isEqual, uniq } from 'lodash'
import * as moment from 'moment-timezone'
import {
  BehaviorSubject,
  combineLatest,
  defer,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs'

import { ElementFormData } from '../prior-authorization-form'
import { PendingAuthorizationAttachment } from './components/auth-attachments'
import { AuthCptPickerValue } from './components/auth-cpt-picker'

export interface AuthorizationDialogData {
  authorization?: AuthorizationModel
  patient: Patient
  patientInsurances: PatientInsurance[]
  appointment: AppointmentModel
  timezone: string
}

@Component({
  selector: 'mmx-authorization-dialog',
  templateUrl: './authorization-dialog.component.html',
  styleUrls: ['./authorization-dialog.component.scss'],
  standalone: false,
})
export class AuthorizationDialogComponent implements OnInit, OnDestroy {
  public loadingAnswers$ = new BehaviorSubject<boolean>(false)
  public currentAnswerStep$ = new BehaviorSubject<string>(null)
  public pendingAttachments$ = new BehaviorSubject<PendingAuthorizationAttachment[]>([])
  public lockingField$ = new BehaviorSubject<string | null>(null)
  public statusPullData: AuthorizationStatusPullData | undefined
  private existingAuthorizations$ = new BehaviorSubject<AuthorizationModel[]>([])
  private appointmentClearances$ = new BehaviorSubject<PatientAccountClearanceModel[] | null>(null)
  private onDestroy$ = new Subject<void>()

  authForm = this.fb.group({
    details: this.fb.control<Partial<SaveAuthorizationParams>>(null),
    clinicalInformation: this.fb.control<Record<string, any>>(null),
  })

  submissionForm = this.fb.group({
    priorAuthorization: this.fb.control<Record<string, any>>(null),
  })

  loadAttachments$ = new Subject<string>()
  attachments$ = this.loadAttachments$.pipe(
    switchMap((authorizationId) => this.authorizationSvc.getAttachments(authorizationId).pipe(
      finalize(() => this.attachmentsLoading$.next(false)),
    )),
    map(({ data }) => data),
    shareReplay(1),
  )
  attachmentsLoading$ = new BehaviorSubject(false)
  saving$ = new BehaviorSubject(false)
  includeUnits = false
  outdated = false
  ignoredOutdatedConflicts = false
  outdatedWarnings: OutdatedFlags[] = []
  availablePortals: PortalModel[] = []
  formData: ElementFormData
  duplicateAuthorizationMessage$ = new BehaviorSubject<string>(null)

  private duplicateAuthorization$ = this.duplicateAuthorizationMessage$.pipe(
    map((message) => !!message),
  )

  saveButtonDisabled$ = combineLatest([
    this.authForm.get('details').statusChanges.pipe(startWith(null)),
    this.saving$,
    this.duplicateAuthorization$,
  ])
    .pipe(
      map(([, saving, duplicateAuthorization]) => this.authForm.get('details').invalid || saving || duplicateAuthorization),
    )

  public get isAuthSubmissionSupported() {
    return false
  }

  public submitToPayerDisabled$ = combineLatest([
    this.submissionForm.statusChanges.pipe(
      startWith(this.submissionForm.status),
    ),
    this.saving$,
  ])
    .pipe(
      map(([status, saving]) => status !== 'VALID' || saving),
    )

  public procedureCodes$ = new BehaviorSubject<AuthCptPickerValue>({})
  public diagnosisCodes$ = new BehaviorSubject<string[]>([])
  public suggestedProcedureCodes$: Observable<string[]> = of([])
  private approvedCptCodes$: Observable<Set<string>> = of(new Set<string>())

  public allQuestions$ = new BehaviorSubject<PortalQuestion[]>([])

  public questions$ = combineLatest([
    this.allQuestions$,
    this.authForm.get('details').valueChanges.pipe(
      startWith(this.authForm.get('details').value),
    ),
  ]).pipe(
    map(([allQuestions]) => {
      const details = this.authForm.get('details').value
      const { procedureCodes = [], diagnosisCodes = [] } = details || {}

      if (!procedureCodes.length && !diagnosisCodes.length) {
        return allQuestions
      }

      return allQuestions.filter((question) => {
        if (
          procedureCodes.length &&
          question.procedureCodes?.length &&
          !question.procedureCodes?.some((code) => Object.keys(procedureCodes).includes(code))) {
          return false
        }

        if (diagnosisCodes.length && question.diagnosesCodes?.length && !question.diagnosesCodes?.some((code) => diagnosisCodes.includes(code))) {
          return false
        }

        return true
      })
    }),
  )

  constructor(
    public dialogRef: MatDialogRef<AuthorizationDialogComponent>,
    public customFieldsService: CustomFieldService,
    public insuranceProviderConfigurationService: InsuranceProviderConfigurationService,
    @Inject(MAT_DIALOG_DATA) public data: AuthorizationDialogData,
    private fb: FormBuilder,
    private authorizationSvc: AppointmentAuthorizationService,
    private insuranceProviderLookupsService: InsuranceProviderLookupsService,
    private necessityService: NecessityService,
    private authorizationRulesService: AuthorizationRulesService,
    private permissionsService: PermissionsService,
    private clinicService: ClinicService,
    private websocketService: WebSocketService,
    private cdr: ChangeDetectorRef,
    private snack: MatSnackBar,
    private translateService: TranslateService,
    private crudService: CrudService,
  ) { }

  public ngOnInit() {
    const { authorization, appointment } = this.data

    this.loadExistingAuthorizations()
    this.watchForDuplicateAuthorizations()

    if (this.permissionsService.instant('auth.manager')) {
      this.authorizationRulesService.collectionParams.next({})
    }

    if (appointment) {
      let procedureCodes = {}
      if (authorization) {
        procedureCodes = (authorization.procedureCodes || []).reduce((acc, code) => ({
          ...acc,
          [code]: { ...(authorization.proceduresData?.[code] ?? {}) },
        }), {})
      } else {
        procedureCodes = this.getAppointmentCptCodes(appointment).reduce((acc, code) => ({
          ...acc,
          [code]: {
            quantity: 1,
            statusCode: null,
          },
        }), {})
      }

      this.procedureCodes$.next(procedureCodes)
      this.diagnosisCodes$.next(_filter(uniq(flatten(appointment.studies?.map(study => study.diagnosisCodes?.map(diagnosis => diagnosis.code))))))
      this.loadAppointmentClearances(appointment)
    }

    this.approvedCptCodes$ = this.buildApprovedCptCodes()
    this.suggestedProcedureCodes$ = this.buildSuggestedProcedureCodes()

    if (!authorization) {
      const primaryInsurance = findInsuranceForAccount(
        this.data.patientInsurances,
        PatientInsuranceType.PRIMARY,
        this.clinicService.clinic,
        appointment?.accountId,
      )

      this.authForm.get('details').patchValue({
        procedureCodes: this.procedureCodes$.getValue(),
        diagnosisCodes: this.diagnosisCodes$.getValue(),
        patientId: appointment?.patientId,
        facilityId: appointment?.locationId,
        insuranceId: primaryInsurance?.id,
        carrierLookupId: primaryInsurance?.carrierLookupId,
        carrierId: primaryInsurance?.carrierId,
      })

      updateFormValidity(this.authForm, false)

      if (this.permissionsService.instant('auth.manager')) {
        this.authForm
          .get('details')
          .valueChanges.pipe(
            takeUntil(this.onDestroy$),
            map((details: Partial<SaveAuthorizationParams>) => details?.insuranceId),
            distinctUntilChanged(),
            filter(Boolean),
          )
          .subscribe((insuranceId) => this.loadNecessity(insuranceId))
      }

      if (appointment?.accountId) {
        this.filterApprovedCptCodesOnInsuranceChange(appointment)
      }

      return
    }

    this.setup(authorization)

    this.authForm.get('details').valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        startWith(this.authForm.get('details').value),
        distinctUntilKeyChanged('carrierLookupId'),
        filter(({ carrierLookupId }) => !!carrierLookupId),
        switchMap(({ carrierLookupId }) => this.insuranceProviderLookupsService.get(carrierLookupId)),
      )
      .subscribe((providerLookup) => {
        this.insuranceProviderConfigurationService.configurationParams$.next({
          providerLookupId: providerLookup.id,
          payerId: providerLookup.provider?.slug,
          type: InsuranceProviderConfiguration.TYPE.SERVICE_REVIEWS,
          subtypeId: InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES,
        })

        this.formData = {
          patient: this.data.patient,
          carrier: providerLookup,
          appointment: this.data.appointment,
          patientInsurance: this.data.patientInsurances.find((insurance) => insurance.carrierLookupId === providerLookup.id),
          authorization: this.data.authorization,
        }
      })

    this.websocketService.clinicianMessage$
      .pipe(
        takeUntil(this.onDestroy$),
        filterAction(WebsocketAction.PRIOR_AUTHORIZATION_SUBMIT),
      ).subscribe((response) => {
        this.saving$.next(false)

        if (response.data?.errors || (response.data as AvailityServiceReview.Review).validationMessages) {
          this.cdr.detectChanges()
          this.openSnackBar(this.translateService.instant('prior-authorization.form.submit-fail'))
          return
        }

        this.openSnackBar(this.translateService.instant('prior-authorization.form.submit-success'))
        this.dialogRef.close()
      })
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public ignoreOutdatedConflicts(): void {
    this.outdated = false
    this.ignoredOutdatedConflicts = true
  }

  public onPendingAttachments(attachments: PendingAuthorizationAttachment[]) {
    this.pendingAttachments$.next(attachments)
  }

  public onNewAttachmentsAdded() {
    if (!this.data.authorization?.id) {
      return
    }

    this.attachmentsLoading$.next(true)
    this.loadAttachments$.next(this.data.authorization.id)
  }

  public onPullStatus() {
    this.saving$.next(true)
    this.websocketService.clinicianMessage$
      .pipe(
        takeUntil(this.onDestroy$),
        filterAction(WebsocketAction.PRIOR_AUTHORIZATION_STATUS),
      )
      .subscribe((response) => {
        this.saving$.next(false)
        if (response.error) {
          this.openSnackBar(this.translateService.instant('prior-authorization.form.pull-status-fail'))
          return
        }

        this.openSnackBar(this.translateService.instant('prior-authorization.form.pull-status-success'))
        if (response.data.authorization) {
          this.setup(new AuthorizationModel(response.data.authorization))
        }
      })

    this.websocketService.messageQueue.next({
      action: WebsocketAction.PRIOR_AUTHORIZATION_STATUS,
      authorizationId: this.formData.authorization.id,
      appointmentId: this.formData.appointment.id,
    })
  }

  public onSubmitToPayer(): void {
    updateFormValidity(this.submissionForm)

    if (this.submissionForm.invalid) {
      return
    }

    this.saving$.next(true)
    const formValue = this.submissionForm.value.priorAuthorization
    const providerLookup = this.formData.carrier
    const serviceReview = {
      payer: {
        id: providerLookup.provider?.slug,
      },
      ...formValue,
    }

    this.websocketService.messageQueue.next({
      action: WebsocketAction.PRIOR_AUTHORIZATION_SUBMIT,
      authorizationId: this.formData.authorization.id,
      serviceReview,
      providerLookupId: providerLookup.id,
      providerId: providerLookup.provider?.id,
    })
  }

  public async onSubmit() {
    updateFormValidity(this.authForm)

    if (this.authForm.invalid) {
      return
    }

    this.saving$.next(true)

    this.handleSubmit()
      .pipe(
        takeUntil(this.onDestroy$),
        withLatestFrom(this.pendingAttachments$),
        switchMap(([authorization, pendingAttachments]) => {
          if (!pendingAttachments.length) {
            return of(authorization)
          }

          return this.authorizationSvc.uploadAuthAttachments(authorization.id, pendingAttachments)
        }),
      )
      .subscribe({
        next: () => {
          this.dialogRef.close(true)
          this.openSnackBar(this.translateService.instant('dialog.authorization.save-success'))
        },
        error: (err: PatientPalRestError) => {
          this.saving$.next(false)
          this.openSnackBar(toSafeUiMessage(err))
        },
      })
  }

  private loadNecessity(insuranceId: string) {
    const appointmentId = this.data.appointment?.id
    if (!appointmentId || !insuranceId) {
      return
    }

    this.necessityService
      .fetch({
        appointmentId,
        insuranceId,
        type: NecessityType.AUTHORIZATION,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response?.data?.status) {
            this.authForm.get('details').patchValue({
              status: response.data.status as AUTHORIZATION_STATUS,
            })
          }
        },
      })
  }

  private handleSubmit(overrides: Partial<SaveAuthorizationParams> = {}) {
    const { startAt, endAt, facility, ...formValue } = this.authForm.get('details').value
    const { questions } = this.authForm.get('clinicalInformation').value || {}

    const ignoredOutdatedIds = this.data.authorization?.ignoredOutdatedIds ?? new Set()
    if (this.ignoredOutdatedConflicts) {
      ignoredOutdatedIds.add(this.data.appointment.id)
    }

    const accountIds = this.data.authorization?.accountIds ?? new Set()

    if (this.data.appointment.accountId != null) {
      accountIds.add(this.data.appointment.accountId)
    }

    const appointmentIds = this.data.authorization?.appointmentIds ?? new Set()
    appointmentIds.add(this.data.appointment.id)

    const params: SaveAuthorizationParams = {
      ...formValue,
      status: formValue.status ?? AUTHORIZATION_STATUS.NONE,
      facilityId: facility?.id,
      startAt: startAt ? moment.utc(startAt.format('YYYY-MM-DD')) : undefined,
      endAt: endAt ? moment.utc(endAt.format('YYYY-MM-DD')) : undefined,
      accountIds: [...accountIds],
      appointmentIds: [...appointmentIds],
      ignoredOutdatedIds: [...ignoredOutdatedIds],
      patientId: this.data.appointment.patientId,
      portalData: questions,
      ...overrides,
    }

    return this.data.authorization
      ? this.authorizationSvc.updateAuthorization(this.data.authorization.id, params)
      : this.authorizationSvc.createAuthorization(params)
  }

  public onUnlockField(field: string): void {
    if (!this.data.authorization) return

    const updatedFields = new Set(this.data.authorization.manuallyEditedFields)
    updatedFields.delete(field)

    this.lockingField$.next(field)
    this.authorizationSvc.patchAuthorizationFields(this.data.authorization.id, [...updatedFields])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (manuallyEditedFields) => {
          this.data.authorization.manuallyEditedFields = new Set(manuallyEditedFields)
          this.lockingField$.next(null)
          this.cdr.detectChanges()
        },
        error: () => {
          this.lockingField$.next(null)
          this.openSnackBar(this.translateService.instant('prior-authorization.form.unlock-field-fail'))
        },
      })
  }

  public onLockField(field: string): void {
    if (!this.data.authorization) return

    const updatedFields = new Set(this.data.authorization.manuallyEditedFields)
    updatedFields.add(field)

    this.lockingField$.next(field)
    this.authorizationSvc.patchAuthorizationFields(this.data.authorization.id, [...updatedFields])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (manuallyEditedFields) => {
          this.data.authorization.manuallyEditedFields = new Set(manuallyEditedFields)
          this.lockingField$.next(null)
          this.cdr.detectChanges()
        },
        error: () => {
          this.lockingField$.next(null)
          this.openSnackBar(this.translateService.instant('prior-authorization.form.lock-field-fail'))
        },
      })
  }

  private setup(authorization: AuthorizationModel) {
    this.outdatedWarnings = []
    const { appointment } = this.data
    const authorizationData = authorization.toJSON()
    const startAt = authorization.dateFrom ? moment.utc(authorization.dateFrom) : undefined
    const endAt = authorization.dateTo ? moment.utc(authorization.dateTo) : undefined

    this.authForm.get('details').patchValue({
      ...authorizationData,
      startAt,
      endAt,
      procedureCodes: this.procedureCodes$.getValue(),
      diagnosisCodes: authorization.diagnosisCodes || this.diagnosisCodes$.getValue(),
      customFields: authorization.customFields,
      // if the authorization does not have a facility, default it to the
      // appointment's location this is a bit strange but facility is a required
      // field so we shouldn't have many situations without a facility. Overall,
      // this provides a smoother user experience
      facility: authorization.facilityId ?? this.data.appointment.location ?? this.data.appointment.locationId,
    })

    updateFormValidity(this.authForm, false)

    if (authorization.limitations) {
      this.includeUnits = true
    }

    this.outdated = authorization.outdatedAppointmentIds.has(appointment.id) && !authorization.ignoredOutdatedIds.has(appointment.id)

    this.authorizationSvc.getAuthorization(authorization.id, appointment.id)
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((extendedAuth) => {
        this.attachmentsLoading$.next(true)
        this.loadAttachments$.next(extendedAuth.id)

        if (extendedAuth.outdatedFlags) {
          this.outdatedWarnings = extendedAuth.outdatedFlags
        }

        if (extendedAuth.portals) {
          this.availablePortals = extendedAuth.portals
        }

        if (extendedAuth.id) {
          this.authForm.get('clinicalInformation').patchValue({
            id: extendedAuth.id,
          })
        }

        if (extendedAuth.questions) {
          this.allQuestions$.next(extendedAuth.questions)
          this.authForm.get('clinicalInformation').patchValue({
            questions: extendedAuth.portalData?.questionAnswers,
          })
        }

        this.statusPullData = extendedAuth.statusPullData

        this.cdr.detectChanges()
      })
  }

  private getAppointmentCptCodes(appointment: AppointmentModel): string[] {
    return appointment?.products?.length
      ? [...new Set(flatten(appointment.products.map((p) => p.getCPTCodes())))].filter(Boolean)
      : []
  }

  /**
   * Account-scoped (not appointment-scoped) so codes already approved on a prior
   * appointment under the same account are still hidden from the picker. Matches the
   * Account Clearance card query (patient-account-clearance-card.component.ts).
   */
  private loadAppointmentClearances(appointment: AppointmentModel): void {
    if (!appointment?.accountId) {
      this.appointmentClearances$.next([])
      return
    }

    this.crudService
      .index(PatientAccountClearanceModel, {
        accountId: appointment.accountId,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (clearances) => this.appointmentClearances$.next(clearances),
        error: () => this.appointmentClearances$.next([]),
      })
  }

  /**
   * A clearance produced by the authorization being edited describes that authorization's own
   * coverage, so it must not hide its codes. Otherwise removing an approved scheduled code
   * would leave the clearance behind and the code could never be suggested again.
   */
  private isOwnClearance(clearance: PatientAccountClearanceModel): boolean {
    const currentAuthorizationId = this.data.authorization?.id

    return !!currentAuthorizationId && clearance.authorizationId === currentAuthorizationId
  }

  /**
   * Suggests the appointment's CPT codes minus those already approved for the selected insurance
   * under a *different* authorization. Those codes are omitted deliberately:
   * `filterApprovedCptCodesOnInsuranceChange()` strips them from the form on the next insurance
   * change, so offering them would suggest a code that cannot persist. This also matches how a new
   * authorization is seeded, keeping create and edit consistent. Clearances produced by the
   * authorization being edited do not hide their own codes — see `isOwnClearance()`.
   */
  private buildSuggestedProcedureCodes(): Observable<string[]> {
    const appointmentCptCodes = this.getAppointmentCptCodes(this.data.appointment)

    if (!appointmentCptCodes.length) {
      return of([])
    }

    return this.approvedCptCodes$.pipe(
      map((approvedCodes) => appointmentCptCodes.filter((code) => !approvedCodes.has(code))),
    )
  }

  private buildApprovedCptCodes(): Observable<Set<string>> {
    const detailsControl = this.authForm.get('details')

    return combineLatest([
      defer(() => detailsControl.valueChanges.pipe(
        startWith(detailsControl.value),
        map((details: Partial<SaveAuthorizationParams>) => details?.insuranceId ?? null),
        distinctUntilChanged(),
      )),
      this.appointmentClearances$.pipe(
        filter((clearances): clearances is PatientAccountClearanceModel[] => clearances !== null),
      ),
    ]).pipe(
      map(([insuranceId, clearances]) => new Set(
        clearances
          .filter((c) => !this.isOwnClearance(c)
            && c.insuranceId === insuranceId
            && c.status === PATIENT_ACCOUNT_PROCEDURE_CLEARANCE_STATUS.APPROVED)
          .map((c) => c.code),
      )),
      takeUntil(this.onDestroy$),
      shareReplay(1),
    )
  }

  private filterApprovedCptCodesOnInsuranceChange(appointment: AppointmentModel): void {
    const appointmentCptCodes = this.getAppointmentCptCodes(appointment)

    if (!appointmentCptCodes.length) {
      return
    }

    const appointmentCptCodesSet = new Set(appointmentCptCodes)
    const detailsControl = this.authForm.get('details')

    this.approvedCptCodes$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((approvedCodes) => {
        // Preserve CPT codes the user manually added (not from the appointment schedule)
        const currentProcedureCodes = (detailsControl.value as Partial<SaveAuthorizationParams>)?.procedureCodes ?? {}
        const manuallyAddedCodes = Object.fromEntries(
          Object.entries(currentProcedureCodes).filter(([code]) => !appointmentCptCodesSet.has(code)),
        )

        const filteredProcedureCodes: AuthCptPickerValue = {
          ...manuallyAddedCodes,
          ...appointmentCptCodes
            .filter((code) => !approvedCodes.has(code))
            .reduce<AuthCptPickerValue>((acc, code) => ({
              ...acc,
              [code]: currentProcedureCodes[code] ?? { quantity: 1, statusCode: null },
            }), {}),
        }

        if (isEqual(currentProcedureCodes, filteredProcedureCodes)) {
          return
        }

        detailsControl.patchValue({ procedureCodes: filteredProcedureCodes }, { emitEvent: false })
      })
  }

  private loadExistingAuthorizations() {
    const patientId = this.data.patient?.id

    if (!patientId) {
      return
    }

    this.authorizationSvc.getAuthorizationList(patientId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (authorizations) => this.existingAuthorizations$.next(authorizations),
        error: () => this.existingAuthorizations$.next([]),
      })
  }

  private watchForDuplicateAuthorizations() {
    const detailsControl = this.authForm.get('details')

    if (!detailsControl) {
      return
    }

    combineLatest([
      detailsControl.valueChanges.pipe(
        startWith(detailsControl.value),
      ),
      this.existingAuthorizations$,
    ])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([details, authorizations]) => {
        this.updateDuplicateAuthorizationMessage(details as Partial<SaveAuthorizationParams>, authorizations)
      })
  }

  private updateDuplicateAuthorizationMessage(
    details: Partial<SaveAuthorizationParams>,
    authorizations: AuthorizationModel[],
  ) {
    if (!details) {
      this.duplicateAuthorizationMessage$.next(null)
      return
    }

    const insuranceId = details.insuranceId
    const referenceNumber = details.referenceNumber?.trim()
    const certificationNumber = details.number?.trim()

    if (!insuranceId || (!referenceNumber && !certificationNumber)) {
      this.duplicateAuthorizationMessage$.next(null)
      return
    }

    const normalizedReferenceNumber = referenceNumber?.toUpperCase()
    const normalizedCertificationNumber = certificationNumber?.toUpperCase()
    let duplicateMessage: string = null

    authorizations.some((authorization) => {
      if (!authorization || authorization.insuranceId !== insuranceId) {
        return false
      }

      if (this.data.authorization?.id && authorization.id === this.data.authorization.id) {
        return false
      }

      if (
        normalizedReferenceNumber &&
        authorization.referenceNumber &&
        authorization.referenceNumber.toUpperCase() === normalizedReferenceNumber
      ) {
        duplicateMessage = `Found existing authorization with reference number ${referenceNumber}`
        return true
      }

      if (
        normalizedCertificationNumber &&
        authorization.number &&
        authorization.number.toUpperCase() === normalizedCertificationNumber
      ) {
        duplicateMessage = `Found existing authorization with certification number ${certificationNumber}`
        return true
      }

      return false
    })

    this.duplicateAuthorizationMessage$.next(duplicateMessage)
  }

  private openSnackBar(message: string) {
    this.snack.open(message, null, { duration: 5000 })
  }

}
